"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import {
  ChevronRight,
  Cpu,
  CreditCard,
  LayoutGrid,
  LogOut,
  Menu,
  MessageSquare,
  Palette,
  Store,
  ShieldCheck,
  X,
  Zap,
} from "lucide-react";
import { useSession } from "@/components/session-provider";
import { BillingPage } from "@/components/billing-page";
import { ErrorNotice, IconButton } from "@/components/ui";
import { errorMessage } from "@/lib/api";

export function PanelShell({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    const desktop = window.matchMedia("(min-width: 801px)");
    const resize = () => {
      if (desktop.matches) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        document.getElementById("navigation-toggle")?.focus();
      }
    };
    desktop.addEventListener("change", resize);
    window.addEventListener("keydown", escape);
    resize();
    return () => {
      desktop.removeEventListener("change", resize);
      window.removeEventListener("keydown", escape);
    };
  }, [open]);
  if (!session.user) return null;
  const links = [
    { href: "/", label: "Servers", icon: LayoutGrid },
    ...(session.can("marketplace.read") ? [{ href: "/marketplace", label: "Marketplace", icon: Store }] : []),
    ...(session.can("discord.manage") ? [{ href: "/settings/discord", label: "Discord", icon: MessageSquare }] : []),
    { href: "/settings/appearance", label: "Appearance", icon: Palette },
    { href: "/billing", label: "Billing", icon: CreditCard },
  ];
  const adminHref = session.tenant ? [
    { href: "/admin/servers", permission: "server.read.all" },
    { href: "/admin/nodes", permission: "node.read" },
    { href: "/admin/templates", permission: "egg.read" },
    { href: "/admin/users", permission: "user.read" },
  ].find((link) => session.can(link.permission))?.href : undefined;
  const staffHref = session.can("platform.read") ? "/staff" : undefined;
  const section = pathname.startsWith("/servers/")
    ? "Server"
    : links.find((link) => link.href === pathname)?.label || "Panel";
  return (
    <div className="panel-shell">
      {open && (
        <button
          className="nav-backdrop"
          aria-label="Close navigation"
          onClick={() => setOpen(false)}
        />
      )}
      <aside
        id="panel-navigation"
        className={`sidebar ${open ? "sidebar-open" : ""}`}
      >
        <Link href="/" className="brand" onClick={() => setOpen(false)}>
          <Zap size={24} />
          <span>
            Velnqo<span className="brand-suffix">Panel</span>
          </span>
        </Link>
        <IconButton
          className="mobile-nav-close"
          label="Close navigation"
          onClick={() => setOpen(false)}
        >
          <X size={18} />
        </IconButton>
        <nav aria-label="Panel navigation">
          <div className="nav-label">Workspace</div>
          {links.map(({ href, label, icon: Icon }) => (
            <Link
              key={href}
              href={href}
              onClick={() => setOpen(false)}
              aria-current={
                pathname === href ||
                (href === "/" && pathname.startsWith("/servers/"))
                  ? "page"
                  : undefined
              }
              className={`nav-item ${pathname === href || (href === "/" && pathname.startsWith("/servers/")) ? "nav-active" : ""}`}
            >
              <Icon size={18} />
              {label}
            </Link>
          ))}
          {(adminHref || staffHref) && (
            <>
              <div className="nav-label nav-label-admin">Management</div>
              {adminHref && <Link href={adminHref} onClick={() => setOpen(false)} className="nav-item nav-switch"><ShieldCheck size={18} />Workspace admin</Link>}
              {staffHref && <Link href={staffHref} onClick={() => setOpen(false)} className="nav-item nav-switch"><ShieldCheck size={18} />Velnqo staff</Link>}
            </>
          )}
        </nav>
        <div className="sidebar-bottom">
          <div className="account">
            <Link
              href="/settings/account"
              className="account-link"
              onClick={() => setOpen(false)}
              aria-current={pathname === "/settings/account" ? "page" : undefined}
            >
              <span className="avatar">
                {(session.user.name || session.user.username)
                  .slice(0, 2)
                  .toUpperCase()}
              </span>
              <div>
                <strong>{session.user.name || session.user.username}</strong>
                <small>
                  {session.can("platform.read") ? "Platform staff" : session.user.tenantRole === "owner" ? "Workspace owner" : "Account"}
                </small>
              </div>
            </Link>
            <IconButton
              label="Sign out"
              onClick={async () => {
                try {
                  await session.logout();
                } catch (error) {
                  setError(errorMessage(error));
                }
              }}
            >
              <LogOut size={16} />
            </IconButton>
          </div>
        </div>
      </aside>
      <div className="panel-main" inert={open}>
        <header className="topbar">
          <IconButton
            className="mobile-nav-toggle"
            id="navigation-toggle"
            aria-expanded={open}
            aria-controls="panel-navigation"
            label="Open navigation"
            onClick={() => setOpen(true)}
          >
            <Menu size={20} />
          </IconButton>
          <div className="breadcrumbs">
            <Cpu size={16} />
            <span>Workspace</span>
            <ChevronRight size={14} />
            <strong>{section}</strong>
          </div>
          <span className="workspace-label">
            <span className="connection-dot" />
            Velnqo Panel
          </span>
        </header>
        <main className="page-content">
          <ErrorNotice message={error} />
          {!session.can("platform.read") && session.tenant?.state !== "active" && !pathname.startsWith("/billing") && pathname !== "/marketplace" && pathname !== "/settings/appearance" && pathname !== "/settings/account" ? <BillingPage /> : children}
        </main>
      </div>
    </div>
  );
}
